import { useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  List,
  Typography,
  useTheme,
  Button,
  Divider,
} from "@mui/material"

import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"

import { PageLayout } from "layouts/PageLayout"

import { LocationIcon } from "components/icons"
import { Head } from "components/Head"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { Carousel, CarouselItem } from "components/Carousel"

import { getIcon } from "utils/icon"
import { InfoItem } from "components/InfoItem"
import { IPageLayoutProps } from "layouts"

interface IPromotionDetailProps {
  Layout?: React.ComponentType<IPageLayoutProps>
  AppNavigation?: React.ComponentType
}

export const PromotionDetail = ({
  Layout = PageLayout,
  AppNavigation,
}: IPromotionDetailProps) => {
  const params = useParams()
  const { promotionId } = params

  const { t } = useTranslation()

  const { promotion, navigateToLocation } = usePromotionDetail(promotionId)

  const theme = useTheme()
  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    card,
    infoContainer,
    infoListItem,
    infoDivider,
    contentWrapper,
    cardContent,
    websiteContainer,
    websiteTitle,
    websiteLinkText,
    cardHeader,
    viewLocationContainer,
    viewLocationButton,
    cardContentPrimaryText,
    cardContentSecondaryText,
    galleryCarouselWrapper,
    galleryCarousel,
    galleryCarouselItem,
    pageLayout,
  } = styleOverrides

  const locationIcon = useMemo(
    () => getIcon(theme?.assets?.locationIcon) || <LocationIcon />,
    [theme]
  )

  const hasManyImages = useMemo(
    () => promotion?.allImages.length > 1,
    [promotion?.allImages]
  )

  if (!promotion) return null

  return (
    <>
      <Head title={`${t(promotion.name)} (${t("Promotion Detail")})`} />
      <Layout
        sx={pageLayout}
        AppNavigation={AppNavigation}
        className={!promotion.hasLocation ? "no-location" : ""}
      >
        <Card sx={card} elevation={0}>
          {promotion.allImages && (
            <Box sx={galleryCarouselWrapper}>
              <Carousel
                sx={galleryCarousel}
                options={{
                  type: "fade",
                  padding: 0,
                  gap: 0,
                  pagination: hasManyImages,
                  autoplay: true,
                  drag: hasManyImages,
                }}
              >
                {promotion.allImages.map(({ id, url }) => {
                  return (
                    <CarouselItem key={`slide-${id}`} sx={galleryCarouselItem}>
                      <CardMedia
                        className={`${promotion.category}`}
                        sx={galleryCarouselItem}
                        component="img"
                        src={url}
                      />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </Box>
          )}
          <Box sx={contentWrapper}>
            <CardHeader sx={cardHeader} title={t(promotion.name)} />

            {promotion.showInfo && (
              <List sx={infoContainer}>
                {!promotion.showAlways && (
                  <InfoItem
                    sx={infoListItem}
                    title={t("Duration")}
                    primary={promotion.durationDate}
                    secondary={promotion.openingText}
                  />
                )}
                {promotion.hasLocation ? (
                  <>
                    <Divider sx={infoDivider} />
                    <InfoItem
                      sx={infoListItem}
                      primary={
                        <LocationInfo
                          feature={promotion.feature}
                          component="span"
                        />
                      }
                      title={t("Location")}
                    />
                  </>
                ) : null}
              </List>
            )}

            {promotion.descriptionText && (
              <CardContent sx={cardContent}>
                <Typography sx={cardContentPrimaryText} gutterBottom>
                  {t("Promotion Detail")}
                </Typography>
                <Typography
                  sx={cardContentSecondaryText}
                  dangerouslySetInnerHTML={{
                    __html: promotion.descriptionText,
                  }}
                />
              </CardContent>
            )}

            {promotion.websiteLink && (
              <Box sx={websiteContainer}>
                <Typography sx={websiteTitle}>{t("Website")}</Typography>
                <Link to={promotion.websiteLink}>
                  <Typography sx={websiteLinkText}>
                    {promotion.websiteLink}
                  </Typography>
                </Link>
              </Box>
            )}
            {promotion.hasLocation && (
              <Box sx={viewLocationContainer}>
                <Button
                  sx={viewLocationButton}
                  variant="contained"
                  startIcon={locationIcon}
                  onClick={navigateToLocation}
                >
                  {t("View Location")}
                </Button>
              </Box>
            )}
          </Box>
        </Card>
      </Layout>
    </>
  )
}
